/**
 * What the workflow screens know about triggers and steps, as plain data
 * and pure functions, so the rules are testable without a page.
 *
 * Mirrors `server/hawfinch-server/src/workflows/mod.rs`: which record each
 * trigger is about (`TriggerKind::record_type`), which steps act on which
 * records (`StepKind::works_on`), each step's `config` shape and the wait
 * step in words (`wait_sentence`). The server checks all of it again on
 * save; this only keeps the screen from offering what it would refuse.
 */
import type { Vocab } from '../../vocab';
import type {
  EmailEventKind, RecordType, StepKind, TriggerKind, WorkflowStep, WorkflowTrigger,
} from '../../api/workflows';

/** The kind of record a run of this trigger is about (an enrolment's: see `triggerRecordType`). */
export function recordTypeOf(kind: TriggerKind): RecordType {
  switch (kind) {
    case 'submission_arrived':
    case 'lead_returned':
    case 'email_event':
    case 'email_link_clicked':
    case 'form_submitted':
      return 'submission';
    case 'enrolment':
      return 'person';
    case 'person_property_changed':
    case 'person_owner_changed':
    case 'score_band_changed':
      return 'person';
    case 'deal_created':
    case 'deal_stage_changed':
    case 'deal_won':
    case 'deal_lost':
      return 'deal';
    case 'task_overdue':
      return 'task';
  }
}

/** The record a workflow with this trigger runs on (`WorkflowTrigger::record_type`). */
export function triggerRecordType(t: WorkflowTrigger): RecordType {
  if (t.kind === 'enrolment') return t.record_type === 'submission' ? 'submission' : 'person';
  return recordTypeOf(t.kind);
}

/** Whether a step can act for a record type (`StepKind::works_on`). */
export function stepWorksOn(kind: StepKind, rt: RecordType): boolean {
  switch (kind) {
    case 'set_property':
    case 'assign_owner':
    case 'send_email':
    case 'send_sms':
      return rt === 'submission' || rt === 'person' || rt === 'deal';
    case 'move_stage':
      return rt === 'deal';
    case 'create_task':
    case 'notify':
    case 'webhook':
    case 'wait':
    case 'wait_until':
    case 'branch':
    case 'end':
    case 'go_to':
    case 'repeat':
      return true;
  }
}

/** A record type in the organisation's words. */
export function recordWord(v: Vocab, rt: RecordType): string {
  switch (rt) {
    case 'submission': return v.one('lead');
    case 'person': return v.one('person');
    case 'deal': return v.one('deal');
    case 'task': return v.one('task');
  }
}

export function recordWordA(v: Vocab, rt: RecordType): string {
  switch (rt) {
    case 'submission': return v.a('lead');
    case 'person': return v.a('person');
    case 'deal': return v.a('deal');
    case 'task': return v.a('task');
  }
}

/** Triggers in the order the picker shows them, grouped by record. */
export const TRIGGER_ORDER: TriggerKind[] = [
  'submission_arrived', 'lead_returned', 'email_event', 'email_link_clicked',
  'person_property_changed', 'person_owner_changed', 'score_band_changed',
  'deal_created', 'deal_stage_changed', 'deal_won', 'deal_lost',
  'task_overdue',
  'form_submitted',
  'enrolment',
];

/** Whether anything starts it yet. Forms arrive in a later phase. */
export function triggerLive(kind: TriggerKind): boolean {
  return kind !== 'form_submitted';
}

/** Whether a wait or a goal can listen for it: an event that happens (`TriggerKind::live`). */
export function eventKind(kind: TriggerKind): boolean {
  return kind !== 'form_submitted' && kind !== 'enrolment';
}

/** A trigger kind as the picker names it. */
export function triggerLabel(v: Vocab, kind: TriggerKind): string {
  switch (kind) {
    case 'submission_arrived': return `A new ${v.one('lead')} arrives`;
    case 'email_event': return 'An email is opened, clicked, bounced or unsubscribed from';
    case 'person_property_changed': return `${v.A('person')}'s ${v.one('property')} changes`;
    case 'person_owner_changed': return `${v.A('person')} is given ${v.a('owner')}`;
    case 'score_band_changed': return `${v.A('person')}'s score band changes`;
    case 'deal_created': return `${v.A('deal')} is created`;
    case 'deal_stage_changed': return `${v.A('deal')} changes ${v.one('stage')}`;
    case 'deal_won': return `${v.A('deal')} is won`;
    case 'deal_lost': return `${v.A('deal')} is lost`;
    case 'task_overdue': return `${v.A('task')} is overdue`;
    case 'form_submitted': return 'Forms, coming soon';
    case 'email_link_clicked': return 'A link in an email is clicked';
    case 'enrolment': return 'Enrolled from a segment, by hand or on a schedule';
    case 'lead_returned': return `A ${v.one('buyer')}'s return of ${v.a('lead')} is approved`;
  }
}

/** A link name as people read it: `book_a_survey` is "Book a survey" (as the server says it). */
export function linkLabel(name: string): string {
  const spaced = name.replace(/[_-]/g, ' ').trim();
  return spaced ? spaced[0]!.toUpperCase() + spaced.slice(1) : '';
}

/**
 * The "link clicked" trigger in words, as the server's `trigger_text` says
 * it: "When someone clicks 'Book a survey' in Swicc welcome".
 */
export function linkClickSentence(t: WorkflowTrigger, templateName?: string | null, campaignName?: string | null): string {
  const link = t.link_name
    ? `clicks '${linkLabel(t.link_name)}'`
    : t.url_contains
      ? `clicks a link containing "${t.url_contains}"`
      : 'clicks a link';
  const place = templateName
    ? ` in ${templateName}`
    : t.email_campaign_id
      ? ` in ${campaignName ?? 'that campaign'}`
      : ' in an email';
  return `When someone ${link}${place}`;
}

/** What a wait or goal listens for, in a few words: "they click 'Book a survey'". */
export function eventWords(v: Vocab, t: WorkflowTrigger): string {
  switch (t.kind) {
    case 'email_link_clicked':
      return t.link_name ? `they click '${linkLabel(t.link_name)}'` : t.url_contains ? `they click a link containing "${t.url_contains}"` : 'they click a link';
    case 'deal_won': return `the ${v.one('deal')} is won`;
    case 'deal_lost': return `the ${v.one('deal')} is lost`;
    case 'deal_stage_changed': return `the ${v.one('deal')} changes ${v.one('stage')}`;
    case 'person_owner_changed': return `they are given ${v.a('owner')}`;
    case 'person_property_changed': return t.property_key ? `their ${t.property_key} changes` : `a ${v.one('property')} of theirs changes`;
    case 'email_event': {
      const ev = t.email_events ?? [];
      if (!ev.length) return 'an email event happens';
      return `they ${ev.map((e) => ({ open: 'open an email', click: 'click an email', bounce: 'bounce', unsubscribe: 'unsubscribe' }[e])).join(' or ')}`;
    }
    default: return triggerLabel(v, t.kind).replace(/^./, (x) => x.toLowerCase());
  }
}

/** A trigger of this kind with none of its own settings. */
export function blankTrigger(kind: TriggerKind): WorkflowTrigger {
  return { kind };
}

/** Steps in the order the Add step menu shows them. */
export const STEP_ORDER: StepKind[] = [
  'set_property', 'assign_owner', 'create_task', 'send_email', 'send_sms', 'notify', 'move_stage', 'webhook', 'wait',
  'wait_until', 'branch', 'repeat', 'go_to', 'end',
];

/** Branches and repeats inside each other, at most this deep (`MAX_BRANCH_DEPTH`). */
export const MAX_BRANCH_DEPTH = 3;

/** The fewest and most passes a Repeat makes (`MIN_REPEAT_TIMES`, `MAX_REPEAT_TIMES`). */
export const MIN_REPEAT_TIMES = 2;
export const MAX_REPEAT_TIMES = 20;

/** How many times a Go to sends the run back when it does not say (`DEFAULT_GO_TO_TIMES`). */
export const DEFAULT_GO_TO_TIMES = 3;
/** The most times one Go to may send the run back (`MAX_GO_TO_TIMES`). */
export const MAX_GO_TO_TIMES = 20;

export function stepLabel(v: Vocab, kind: StepKind | string): string {
  switch (kind) {
    case 'set_property': return `Set ${v.one('property')}`;
    case 'assign_owner': return `Assign ${v.one('owner')}`;
    case 'create_task': return `Create ${v.one('task')}`;
    case 'send_email': return 'Send email';
    case 'send_sms': return 'Send SMS';
    case 'notify': return 'Notify someone';
    case 'move_stage': return `Move ${v.one('deal')} ${v.one('stage')}`;
    case 'webhook': return 'Call a webhook';
    case 'wait': return 'Wait';
    case 'wait_until': return 'Wait until';
    case 'branch': return 'If / else';
    case 'end': return 'End';
    case 'go_to': return 'Go to';
    case 'repeat': return 'Repeat';
    case 'conditions': return 'Conditions';
    case 'goal': return 'Goal';
    default: return kind;
  }
}

/**
 * The steps the Add step menu offers for a record type. SMS only once an
 * SMS provider exists (`sms_available`); until then it is not shown at all.
 * A list already `depth` levels deep (branches and repeats) offers neither
 * at the limit, and a list inside a repeat offers no further repeat.
 */
export function stepsOffered(rt: RecordType, smsAvailable: boolean, depth = 0, inRepeat = false): StepKind[] {
  return STEP_ORDER.filter((k) =>
    stepWorksOn(k, rt)
    && (k !== 'send_sms' || smsAvailable)
    && ((k !== 'branch' && k !== 'repeat') || depth < MAX_BRANCH_DEPTH)
    && (k !== 'repeat' || !inRepeat));
}

// ---------------------------------------------------------------------------
// Steps as a tree
// ---------------------------------------------------------------------------

/** The lists a step can hold: a branch's `yes` and `no`, a repeat's `body`. */
export const SIDES = ['yes', 'no', 'body'] as const;
export type Side = (typeof SIDES)[number];

/**
 * Every step, those inside branches and repeats too, in the order they are
 * written (a step, its yes steps, its no steps, its body, then the next),
 * which is how the server numbers them.
 */
export function allSteps(steps: WorkflowStep[]): WorkflowStep[] {
  return steps.flatMap((s) => [s, ...allSteps(s.yes ?? []), ...allSteps(s.no ?? []), ...allSteps(s.body ?? [])]);
}

/** Where a list of steps is: the top, one side of a branch, or a repeat's body. */
export type ListRef = { parentId: string | null; side?: Side };

function holdsSteps(s: WorkflowStep): boolean {
  return SIDES.some((side) => (s[side]?.length ?? 0) > 0);
}

/** A step with `f` applied to each list inside it that has steps. */
function mapInner(s: WorkflowStep, f: (list: WorkflowStep[]) => WorkflowStep[]): WorkflowStep {
  if (!holdsSteps(s)) return s;
  const next = { ...s };
  for (const side of SIDES) {
    if (s[side]?.length) next[side] = f(s[side]!);
  }
  return next;
}

function mapLists(steps: WorkflowStep[], at: ListRef, f: (list: WorkflowStep[]) => WorkflowStep[]): WorkflowStep[] {
  if (at.parentId === null) return f(steps);
  return steps.map((s) => {
    if (s.id === at.parentId && at.side) {
      return { ...s, [at.side]: f(s[at.side] ?? []) };
    }
    return mapInner(s, (list) => mapLists(list, at, f));
  });
}

/**
 * Add a step at the end of one list. A new "wait until" on an email event
 * listens to the email of the nearest email step before it on its path
 * (`email_from`), so it is not woken by an older email or another
 * workflow's; with none before it, any email.
 */
export function addStepAt(steps: WorkflowStep[], at: ListRef, step: WorkflowStep): WorkflowStep[] {
  return mapLists(steps, at, (list) => [...list, withEmailFrom(steps, at, list.length, step)]);
}

/** Whether a wait on this event can say whose email it listens to (`is_email_kind`). */
export function isEmailEvent(ev: unknown): boolean {
  const kind = ev && typeof ev === 'object' ? (ev as { kind?: unknown }).kind : undefined;
  return kind === 'email_event' || kind === 'email_link_clicked';
}

/** A new wait until, about to go at `index` in list `at`, with its `email_from` filled in. */
export function withEmailFrom(steps: WorkflowStep[], at: ListRef, index: number, step: WorkflowStep): WorkflowStep {
  const c = cfg(step);
  if (step.kind !== 'wait_until' || c.email_from !== undefined || !isEmailEvent(c.event)) return step;
  const send = nearestSendBefore(steps, at, index);
  return send ? { ...step, config: { ...c, email_from: send } } : step;
}

type PathPart = { list: WorkflowStep[]; upTo: number };

/** The lists from the top down to list `at`, each with how far along the path goes in it. */
function pathTo(list: WorkflowStep[], at: ListRef, index: number): PathPart[] | null {
  for (let i = 0; i < list.length; i += 1) {
    const s = list[i]!;
    if (s.id === at.parentId && at.side) {
      const inner = s[at.side] ?? [];
      return [{ list, upTo: i }, { list: inner, upTo: Math.min(index, inner.length) }];
    }
    for (const side of SIDES) {
      const r = pathTo(s[side] ?? [], at, index);
      if (r) return [{ list, upTo: i }, ...r];
    }
  }
  return null;
}

/**
 * The nearest email step before position `index` in list `at`, on the way
 * to it: earlier in the same list, else before the branch or repeat that
 * holds it, and so on up. An email step inside an earlier branch is not on
 * every path, so it does not count.
 */
export function nearestSendBefore(steps: WorkflowStep[], at: ListRef, index: number): string | null {
  const path = at.parentId === null
    ? [{ list: steps, upTo: Math.min(index, steps.length) }]
    : pathTo(steps, at, index);
  for (const part of [...(path ?? [])].reverse()) {
    for (let j = part.upTo - 1; j >= 0; j -= 1) {
      const s = part.list[j]!;
      if (s.kind === 'send_email') return s.id;
    }
  }
  return null;
}

/** Change one step, wherever it is. */
export function updateStep(steps: WorkflowStep[], id: string, f: (s: WorkflowStep) => WorkflowStep): WorkflowStep[] {
  return steps.map((s) => (s.id === id ? f(s) : mapInner(s, (list) => updateStep(list, id, f))));
}

/** Take a step out, with whatever is inside it. */
export function removeStep(steps: WorkflowStep[], id: string): WorkflowStep[] {
  return steps
    .filter((s) => s.id !== id)
    .map((s) => mapInner(s, (list) => removeStep(list, id)));
}

/** Move a step up or down within its own list. */
export function moveStep(steps: WorkflowStep[], id: string, by: -1 | 1): WorkflowStep[] {
  const i = steps.findIndex((s) => s.id === id);
  if (i >= 0) {
    const j = i + by;
    if (j < 0 || j >= steps.length) return steps;
    const next = [...steps];
    [next[i], next[j]] = [next[j]!, next[i]!];
    return next;
  }
  return steps.map((s) => mapInner(s, (list) => moveStep(list, id, by)));
}

/** Put a step into one list at a place (clamped to the list). */
export function insertStepAt(steps: WorkflowStep[], at: ListRef, index: number, step: WorkflowStep): WorkflowStep[] {
  return mapLists(steps, at, (list) => {
    const i = Math.max(0, Math.min(index, list.length));
    return [...list.slice(0, i), withEmailFrom(steps, at, i, step), ...list.slice(i)];
  });
}

/** Where a step is: its list and its place in it, or null. */
export function locateStep(steps: WorkflowStep[], id: string, at: ListRef = { parentId: null }): { at: ListRef; index: number } | null {
  for (let i = 0; i < steps.length; i += 1) {
    const s = steps[i]!;
    if (s.id === id) return { at, index: i };
    for (const side of SIDES) {
      const found = locateStep(s[side] ?? [], id, { parentId: s.id, side });
      if (found) return found;
    }
  }
  return null;
}

/**
 * Move a step, with whatever is inside it, to a place in any list. Refused
 * (the steps come back unchanged) when the place is inside the step itself,
 * when it would nest branches and repeats deeper than allowed, or put a
 * repeat inside a repeat.
 */
export function moveStepTo(steps: WorkflowStep[], id: string, at: ListRef, index: number): WorkflowStep[] {
  const from = locateStep(steps, id);
  if (!from) return steps;
  const step = allSteps(steps).find((s) => s.id === id)!;
  if (at.parentId !== null && allSteps([step]).some((s) => s.id === at.parentId)) return steps;
  const sameList = from.at.parentId === at.parentId && from.at.side === at.side;
  const to = sameList && from.index < index ? index - 1 : index;
  if (sameList && to === from.index) return steps;
  const next = insertStepAt(removeStep(steps, id), at, to, step);
  if (repeatInRepeat(next) && !repeatInRepeat(steps)) return steps;
  return branchDepth(next) > Math.max(MAX_BRANCH_DEPTH, branchDepth(steps)) ? steps : next;
}

/** How deep the branches and repeats go, each a level (0 with none). */
export function branchDepth(steps: WorkflowStep[]): number {
  return steps.reduce((d, s) => (s.kind === 'branch' || s.kind === 'repeat'
    ? Math.max(d, 1 + Math.max(...SIDES.map((side) => branchDepth(s[side] ?? []))))
    : d), 0);
}

/** The ids of the steps inside a repeat's body, each with its repeat's id. */
export function repeatOf(steps: WorkflowStep[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const s of allSteps(steps)) {
    if (s.kind === 'repeat') for (const inner of allSteps(s.body ?? [])) out.set(inner.id, s.id);
  }
  return out;
}

/** Whether a repeat sits inside another repeat, which the server refuses. */
export function repeatInRepeat(steps: WorkflowStep[]): boolean {
  const within = repeatOf(steps);
  return allSteps(steps).some((s) => s.kind === 'repeat' && within.has(s.id));
}

/**
 * Whether every way through these steps passes a Wait or a Wait until, or
 * ends (`always_waits`): what keeps a Repeat from going round instantly. A
 * branch counts when both its paths do.
 */
export function alwaysWaits(steps: WorkflowStep[]): boolean {
  return steps.some((s) => {
    switch (s.kind) {
      case 'wait':
      case 'wait_until':
      case 'end':
        return true;
      case 'branch':
        return alwaysWaits(s.yes ?? []) && alwaysWaits(s.no ?? []);
      case 'repeat':
        return alwaysWaits(s.body ?? []);
      default:
        return false;
    }
  });
}

/**
 * The first email step in written order. Unless it says otherwise, it is
 * the one sent once per person; later ones are not (`once_per_person`).
 */
export function firstSendId(steps: WorkflowStep[]): string | null {
  return allSteps(steps).find((s) => s.kind === 'send_email')?.id ?? null;
}

/** Whether anything in the tree sends email (such a workflow always exits on unsubscribe or bounce). */
export function sendsEmail(steps: WorkflowStep[]): boolean {
  return allSteps(steps).some((s) => s.kind === 'send_email');
}

type WithId = { id: string; yes?: WithId[] | null; no?: WithId[] | null; body?: WithId[] | null };

function idsOf(list: WithId[]): string[] {
  return list.flatMap((s) => [s.id, ...idsOf(s.yes ?? []), ...idsOf(s.no ?? []), ...idsOf(s.body ?? [])]);
}

/** A new step id, unique across the whole workflow, branches and repeats included. */
export function newStepId(existing: WithId[]): string {
  const ids = idsOf(existing);
  const taken = new Set(ids);
  for (let n = ids.length + 1; ; n += 1) {
    const id = `step-${n}`;
    if (!taken.has(id)) return id;
  }
}

/** A step of this kind with the settings the server reads, at their defaults. */
export function blankStep(kind: StepKind, existing: WithId[], rt: RecordType): WorkflowStep {
  const id = newStepId(existing);
  switch (kind) {
    case 'set_property':
      return { id, kind, config: { object: 'person', key: '', value: '' } };
    case 'assign_owner':
      // A rotation gives people to reps; a deal goes to a named user.
      return { id, kind, config: rt === 'deal' ? { mode: 'user', user_id: '' } : { mode: 'rotation', group_id: '' } };
    case 'create_task':
      return { id, kind, config: { subject: 'Call {{name}}', due_amount: 1, due_unit: 'hours', owner: 'record_owner' } };
    case 'send_email':
      // Once per person is left unsaid: the first email step is, later ones are not.
      return { id, kind, config: { template_id: '' } };
    case 'send_sms':
      return { id, kind, config: { message: '' } };
    case 'notify':
      return { id, kind, config: { to: 'owner', title: '', body: '' } };
    case 'move_stage':
      return { id, kind, config: { stage_id: '' } };
    case 'webhook':
      return { id, kind, config: { url: '' } };
    case 'wait':
      return { id, kind, config: { amount: 1, unit: 'days' } };
    case 'wait_until':
      return { id, kind, config: { event: { kind: 'email_link_clicked' }, amount: 3, unit: 'days' } };
    case 'branch':
      return { id, kind, config: { conditions: [], wait_matched: true }, yes: [], no: [] };
    case 'end':
      return { id, kind, config: { outcome: 'done' } };
    case 'go_to':
      return { id, kind, config: { target: '', max_times: DEFAULT_GO_TO_TIMES } };
    case 'repeat':
      return { id, kind, config: { times: MIN_REPEAT_TIMES }, body: [] };
  }
}

/** A wait-until in words: "Wait until they click 'Book a survey', or 3 days". */
export function waitUntilSentence(v: Vocab, c: Record<string, unknown>): string {
  const ev = c.event as WorkflowTrigger | null | undefined;
  const what = ev && typeof ev === 'object' ? eventWords(v, ev) : 'the conditions are true';
  const amount = Math.trunc(num(c.amount));
  const unitRaw = str(c.unit) || 'days';
  const unit = amount === 1 ? unitRaw.replace(/s$/, '') : unitRaw;
  return `Wait until ${what}, or ${amount} ${unit}`;
}

/** A step's config as an object, whatever the server sent. */
export function cfg(step: Pick<WorkflowStep, 'config'>): Record<string, unknown> {
  return step.config && typeof step.config === 'object' && !Array.isArray(step.config)
    ? (step.config as Record<string, unknown>)
    : {};
}

export function str(v: unknown): string {
  return typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
}

export function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** `HH:MM`, 24-hour, as the server reads it (`parse_time_of_day`). */
export function parseTimeOfDay(raw: string | null | undefined): [number, number] | null {
  const t = (raw ?? '').trim();
  const at = t.indexOf(':');
  if (at < 0) return null;
  const h = t.slice(0, at);
  const m = t.slice(at + 1);
  if (h.length === 0 || h.length > 2 || m.length !== 2 || !/^\d+$/.test(h) || !/^\d+$/.test(m)) return null;
  const hh = Number(h);
  const mm = Number(m);
  return hh < 24 && mm < 60 ? [hh, mm] : null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * A wait step in words, exactly as the server says it (`wait_sentence`):
 * "Wait 2 days, then at 09:00 (UK time)".
 */
export function waitSentence(c: { amount?: unknown; unit?: unknown; at_time?: unknown }): string {
  const amount = Math.trunc(num(c.amount));
  const unitRaw = str(c.unit);
  const unit = unitRaw === 'minutes'
    ? (amount === 1 ? 'minute' : 'minutes')
    : unitRaw === 'hours'
      ? (amount === 1 ? 'hour' : 'hours')
      : unitRaw === 'days' && amount === 1 ? 'day' : 'days';
  const first = amount > 0 ? `Wait ${amount} ${unit}` : '';
  const time = parseTimeOfDay(str(c.at_time));
  if (first && time) return `${first}, then at ${pad(time[0])}:${pad(time[1])} (UK time)`;
  if (!first && time) return `Wait until ${pad(time[0])}:${pad(time[1])} (UK time)`;
  if (first) return first;
  return 'Wait';
}

export const EMAIL_EVENTS: { value: EmailEventKind; label: string }[] = [
  { value: 'open', label: 'Opened' },
  { value: 'click', label: 'Clicked' },
  { value: 'bounce', label: 'Bounced' },
  { value: 'unsubscribe', label: 'Unsubscribed' },
];

export const UNITS = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

/**
 * Settings a template cannot know, as `step_id.setting` pairs
 * (`assign.group_id`), split for the form that asks for them.
 */
export function parseNeeds(needs: string[]): { stepId: string; key: string }[] {
  return needs
    .map((n) => {
      const at = n.indexOf('.');
      return at > 0 ? { stepId: n.slice(0, at), key: n.slice(at + 1) } : null;
    })
    .filter((x): x is { stepId: string; key: string } => x !== null);
}

/** A template's steps with the chosen settings filled in. */
export function fillNeeds(steps: WorkflowStep[], filled: Record<string, string>): WorkflowStep[] {
  return steps.map((s) => {
    const mine = Object.entries(filled).filter(([k]) => k.startsWith(`${s.id}.`));
    if (mine.length === 0) return s;
    const next = { ...cfg(s) };
    for (const [k, value] of mine) next[k.slice(s.id.length + 1)] = value;
    return { ...s, config: next };
  });
}

/**
 * What still needs filling before a definition can be saved, in words, or
 * an empty list. The server checks again; this is so Save says why up front.
 */
export function stepProblems(v: Vocab, steps: WorkflowStep[], rt: RecordType, stageKind: (id: string) => string | null): string[] {
  const out: string[] = [];
  if (branchDepth(steps) > MAX_BRANCH_DEPTH) out.push(`Branches and repeats can be nested at most ${MAX_BRANCH_DEPTH} deep.`);
  const ids = new Set(allSteps(steps).map((s) => s.id));
  const within = repeatOf(steps);
  allSteps(steps).forEach((s, i) => {
    const n = `Step ${i + 1}`;
    const c = cfg(s);
    if (!stepWorksOn(s.kind, rt)) {
      out.push(`${n} cannot act on ${recordWordA(v, rt)}.`);
      return;
    }
    switch (s.kind) {
      case 'set_property':
        if (!str(c.key)) out.push(`${n}: choose a ${v.one('property')}.`);
        break;
      case 'assign_owner':
        if (c.mode === 'rotation' && rt === 'deal') out.push(`${n}: a rotation gives ${v.many('person')} to reps; for ${v.a('deal')} choose one person.`);
        else if (c.mode === 'rotation' && !str(c.group_id)) out.push(`${n}: choose a rotation.`);
        if (c.mode === 'user' && !str(c.user_id)) out.push(`${n}: choose who.`);
        break;
      case 'create_task':
        if (!str(c.subject).trim()) out.push(`${n}: give the ${v.one('task')} a subject.`);
        if (c.owner === 'user' && !str(c.user_id)) out.push(`${n}: choose who the ${v.one('task')} is for.`);
        break;
      case 'send_email':
        if (!str(c.template_id)) out.push(`${n}: choose an email template.`);
        if (c.max_age_hours != null && !(Number.isInteger(c.max_age_hours) && num(c.max_age_hours) >= 1 && num(c.max_age_hours) <= 168)) {
          out.push(`${n}: give up after between 1 and 168 hours, or leave it empty.`);
        }
        break;
      case 'notify':
        if (!str(c.title).trim()) out.push(`${n}: give the notification a title.`);
        if (c.to === 'user' && !str(c.user_id)) out.push(`${n}: choose who to notify.`);
        break;
      case 'move_stage':
        if (!str(c.stage_id)) out.push(`${n}: choose a ${v.one('stage')}.`);
        else if (stageKind(str(c.stage_id)) === 'lost' && !str(c.reason).trim()) {
          out.push(`${n}: moving to a lost ${v.one('stage')} needs a reason.`);
        }
        break;
      case 'webhook':
        if (!/^https?:\/\/\S+$/i.test(str(c.url).trim())) out.push(`${n}: enter the address to call, starting https://.`);
        break;
      case 'wait': {
        const time = str(c.at_time).trim();
        if (time && !parseTimeOfDay(time)) out.push(`${n}: a time of day is HH:MM, 24-hour.`);
        else if (num(c.amount) <= 0 && !time) out.push(`${n}: wait for some time, or until a time of day.`);
        break;
      }
      case 'send_sms':
        out.push(`${n}: sending SMS is not available yet.`);
        break;
      case 'wait_until': {
        const hasEvent = !!c.event;
        const hasConds = Array.isArray(c.conditions) && c.conditions.length > 0;
        if (!hasEvent && !hasConds) out.push(`${n}: choose what to wait for.`);
        if (num(c.amount) <= 0) out.push(`${n}: give the wait a time limit.`);
        const ev = c.event as WorkflowTrigger | undefined;
        if (ev?.kind === 'email_link_clicked' && ev.link_name && !/^[A-Za-z0-9_-]{1,100}$/.test(ev.link_name)) {
          out.push(`${n}: a link name is letters, numbers, _ and -.`);
        }
        const from = str(c.email_from);
        if (isEmailEvent(ev) && from && from !== 'any' && from !== 'this_run') {
          const sender = allSteps(steps).find((x) => x.id === from);
          if (!sender) out.push(`${n}: the email step it listens to has been removed.`);
          else if (sender.kind !== 'send_email') out.push(`${n}: the step it listens to does not send an email.`);
        }
        break;
      }
      case 'branch': {
        const hasConds = Array.isArray(c.conditions) && c.conditions.length > 0;
        if (!hasConds && (c.wait_matched === undefined || c.wait_matched === null)) {
          out.push(`${n}: a branch needs conditions, or the last wait to look at.`);
        }
        break;
      }
      case 'go_to': {
        const target = str(c.target);
        if (!target) out.push(`${n}: choose the step to go back to.`);
        else if (target === s.id) out.push(`${n}: a step cannot go to itself.`);
        else if (!ids.has(target)) out.push(`${n}: the step to go back to has been removed.`);
        else if (within.has(target) && within.get(target) !== within.get(s.id)) {
          out.push(`${n}: a Go to cannot jump into a Repeat from outside it. Go to the Repeat step itself instead.`);
        }
        const times = c.max_times;
        if (times !== undefined && times !== null && (num(times) < 1 || num(times) > MAX_GO_TO_TIMES)) {
          out.push(`${n}: go back between 1 and ${MAX_GO_TO_TIMES} times.`);
        }
        break;
      }
      case 'end':
        if (c.outcome !== 'done' && c.outcome !== 'exited') out.push(`${n}: choose how it ends.`);
        break;
      case 'repeat': {
        const times = num(c.times);
        if (!Number.isInteger(times) || times < MIN_REPEAT_TIMES || times > MAX_REPEAT_TIMES) {
          out.push(`${n}: do the steps between ${MIN_REPEAT_TIMES} and ${MAX_REPEAT_TIMES} times.`);
        }
        if (within.has(s.id)) out.push(`${n}: a Repeat cannot go inside another Repeat.`);
        if (!s.body?.length) out.push(`${n}: add the steps to repeat.`);
        else if (!alwaysWaits(s.body)) out.push(`${n}: a Repeat needs a Wait on every way round, or it would repeat instantly.`);
        break;
      }
    }
  });
  return out;
}

/** A run's status as a badge reads it, and its colour. */
export const RUN_STATUS: Record<string, { label: string; color: string }> = {
  running: { label: 'Running', color: 'chartBlue' },
  waiting: { label: 'Waiting', color: 'chartAmber' },
  done: { label: 'Done', color: 'chartGreen' },
  failed: { label: 'Failed', color: 'red' },
  cancelled: { label: 'Cancelled', color: 'gray' },
  goal_reached: { label: 'Goal reached', color: 'teal' },
  exited: { label: 'Exited', color: 'gray' },
};

/** A step log line's status, as a badge reads it. */
export const LOG_STATUS: Record<string, { label: string; color: string }> = {
  done: { label: 'Done', color: 'chartGreen' },
  skipped: { label: 'Skipped', color: 'gray' },
  retrying: { label: 'Retrying', color: 'chartAmber' },
  failed: { label: 'Failed', color: 'red' },
  waiting: { label: 'Waiting', color: 'chartAmber' },
  goal_reached: { label: 'Goal reached', color: 'teal' },
  exited: { label: 'Exited', color: 'gray' },
};
